import { ApiRequestError, broadcastTransaction, waitForMempoolAcceptance } from "./api";
import { verifiedInscriptionTransactionStatus } from "./history";
import type { SignedPackage } from "./types";
import { recoveryTransaction, type RefundDestination, type RefundQuote } from "./recovery-refund";

async function recoveryRequest<T>(action: "quote" | "submit", authorization: string, body: object): Promise<T> {
  const response = await fetch(`/api/recovery/refund/${action}`, { method: "POST", headers: { "content-type": "application/json", authorization: `Bearer ${authorization}` }, body: JSON.stringify(body) });
  const result = await response.json() as { error?: unknown };
  if (!response.ok) throw new Error(typeof result.error === "string" ? result.error : "Recovery request failed; preserve your saved package");
  return result as T;
}
export const quoteRecoveryRefund = (saved: SignedPackage, auth: string, destination: RefundDestination, feeRate: number) =>
  recoveryRequest<RefundQuote>("quote", auth, { package: saved, destination, feeRate });
export const submitRecoveryRefund = (saved: SignedPackage, auth: string, quote: RefundQuote, raw: string) =>
  recoveryRequest<{ txid: string; alreadyKnown: boolean }>("submit", auth, { package: saved, authorization: quote.authorization, raw });

type CommitState = "confirmed" | "mempool" | "accepted" | "unknown";
export type RecoveryStage = "broadcasting-commit" | "broadcasting-reveal" | "retrying-reveal" | "verifying-commit" | "verifying-reveal" | "retrying-commit";

export class RecoveryRequiredError extends Error {
  readonly saved: SignedPackage;
  constructor(saved: SignedPackage, commitState: CommitState, caught: unknown, revealWasNotSubmitted = false) {
    const state = commitState === "confirmed" ? "Payment/commit confirmed."
      : commitState === "mempool" ? "Payment/commit accepted; awaiting confirmation."
        : commitState === "accepted" ? "Payment/commit accepted." : "Payment/commit status is not yet verified.";
    const revealState = revealWasNotSubmitted || caught instanceof ApiRequestError && caught.revealNotSent
      ? "Reveal not sent." : "Reveal submission is not yet verified.";
    const detail = caught instanceof Error ? caught.message.replace(/\s*Keep the saved transaction package and use Transaction Recovery; do not pay again\.$/, "") : "Validation is temporarily unavailable.";
    super(`${state} ${revealState} Do not pay again. Use Transaction Recovery to resume the saved package. ${detail}`);
    this.name = "RecoveryRequiredError";
    this.saved = { ...saved, recovery: undefined };
  }
}

class TemporaryStatusError extends Error {}

async function transactionStatus(txid: string): Promise<{ confirmed: boolean } | null> {
  if (!/^[0-9a-f]{64}$/i.test(txid)) throw new Error("Invalid saved transaction ID");
  let response: Response;
  try { response = await fetch(`/api/tx/${txid}/status`, { cache: "no-store" }); }
  catch { throw new TemporaryStatusError("Transaction status is temporarily unavailable. Check again before resuming."); }
  if (response.status === 404) return null;
  if (!response.ok) {
    if (response.status === 429 || response.status >= 500) throw new TemporaryStatusError("Transaction status is temporarily unavailable. Check again before resuming.");
    throw new Error("Transaction status could not be verified. Check again before resuming.");
  }
  const status = verifiedInscriptionTransactionStatus(txid, await response.json());
  if (!status) throw new Error("Transaction presence could not be verified. Refresh the app and check again before resuming.");
  return { confirmed: status.confirmed };
}

const pause = (ms: number) => new Promise<void>(resolve => globalThis.setTimeout(resolve, ms));
function canReconcile(caught: unknown): caught is ApiRequestError {
  return caught instanceof ApiRequestError && caught.broadcastStatus === "unknown" && caught.reconcilableSubmission;
}

/** Read-only after an uncertain send. Unknown GET results never become absence. */
async function reconcilePresence(txid: string, rawHex: string) {
  let unavailable: TemporaryStatusError | undefined;
  for (let attempt = 0; attempt < 3; attempt++) {
    if (attempt) await pause(attempt === 1 ? 2_000 : 5_000);
    try {
      let response: Response;
      try { response = await fetch(`/api/tx/${txid}/hex`, { cache: "no-store" }); }
      catch { throw new TemporaryStatusError("Bitcoin acceptance is temporarily unavailable. Keep the saved transaction."); }
      if (response.status === 404) {
        const missing: unknown = await response.json();
        if (!missing || typeof missing !== "object" || !("txid" in missing) || missing.txid !== txid
          || !("found" in missing) || missing.found !== false) throw new Error("Exact acceptance lookup is unavailable. Refresh the app and keep the saved package.");
        continue;
      }
      if (response.status === 429 || response.status >= 500) throw new TemporaryStatusError("Bitcoin acceptance is temporarily unavailable. Keep the saved transaction.");
      if (!response.ok) throw new Error("Bitcoin acceptance could not be verified. Keep the saved transaction.");
      const observed: unknown = await response.json();
      if (!observed || typeof observed !== "object" || !("found" in observed) || observed.found !== true
        || !("txid" in observed) || observed.txid !== txid || !("hex" in observed) || typeof observed.hex !== "string"
        || observed.hex.toLowerCase() !== rawHex.toLowerCase()) throw new Error("Bitcoin did not return the exact saved transaction. Keep the saved package.");
      return true;
    } catch (caught) {
      if (!(caught instanceof TemporaryStatusError)) throw caught;
      unavailable = caught;
    }
  }
  if (unavailable) throw unavailable;
  return false;
}

/** Both normal submission and recovery use the exact saved pair; never build, authorize or sign here. */
export async function resumeRecoveryPackage(saved: SignedPackage, onStage?: (stage: RecoveryStage) => void): Promise<string> {
  let commitState: CommitState = "unknown";
  let revealStarted = false;
  try {
    if (recoveryTransaction(saved.commitHex).id !== saved.commitTxid || recoveryTransaction(saved.revealHex).id !== saved.revealTxid) {
      throw new Error("Recovery transaction IDs do not match the saved transactions");
    }
    if (await transactionStatus(saved.revealTxid)) return saved.revealTxid;
    if (!saved.commitAuthorization) throw new Error("This package has no STC authorization. Preserve it; automated recovery cannot safely submit it.");
    const commit = await transactionStatus(saved.commitTxid);
    if (commit) commitState = commit.confirmed ? "confirmed" : "mempool";
    else {
      let commitObserved = false;
      for (let attempt = 0; ; attempt++) {
        onStage?.("broadcasting-commit");
        try {
          const txid = (await broadcastTransaction(saved.commitHex, {
            role: "commit", authorization: saved.commitAuthorization, revealTxHex: saved.revealHex,
          })).trim();
          if (txid !== saved.commitTxid) throw new Error("Unexpected commit response. Check transaction status before retrying.");
          commitState = "mempool";
          break;
        } catch (caught) {
          if (!canReconcile(caught)) throw caught;
          onStage?.("verifying-commit");
          const observed = await reconcilePresence(saved.commitTxid, saved.commitHex);
          if (observed) { commitState = "accepted"; commitObserved = true; break; }
          if (attempt >= 1) throw caught;
          // Same signed bytes and authorization, through fresh Worker parent
          // validation. Never replay a commit directly at the provider.
          onStage?.("retrying-commit");
          await pause(2_000);
        }
      }
      if (!commitObserved) await waitForMempoolAcceptance(saved.commitTxid, 6);
    }
    for (let attempt = 0; ; attempt++) {
      onStage?.("broadcasting-reveal");
      revealStarted = true;
      try {
        const txid = (await broadcastTransaction(saved.revealHex, {
          role: "reveal", authorization: saved.commitAuthorization, commitTxHex: saved.commitHex,
        })).trim();
        if (txid !== saved.revealTxid) throw new Error("Unexpected reveal response. Check transaction status before retrying.");
        return txid;
      } catch (caught) {
        if (canReconcile(caught)) {
          onStage?.("verifying-reveal");
          if (await reconcilePresence(saved.revealTxid, saved.revealHex)) return saved.revealTxid;
          if (attempt >= 2) throw caught;
          onStage?.("retrying-reveal");
          await pause(2_000);
          continue;
        }
        // Only a structured pre-submission index outage is safe to retry here.
        // Every retry re-enters Worker authorization and live input validation.
        if (!(caught instanceof ApiRequestError) || !caught.revealNotSent || !caught.retryableValidation || attempt >= 2) throw caught;
        onStage?.("retrying-reveal");
        await new Promise<void>(resolve => globalThis.setTimeout(resolve, attempt === 0 ? 2_000 : 5_000));
        // Another request/tab may have finished while we waited. A failed or
        // malformed lookup remains unknown, never evidence to send again.
        if (await transactionStatus(saved.revealTxid)) return saved.revealTxid;
      }
    }
  } catch (caught) { throw new RecoveryRequiredError(saved, commitState, caught, !revealStarted); }
}
